import React, {useMemo, useState} from 'react';
import {Button, Spin, Text, TableColumnConfig, Checkbox, DropdownMenu, Icon} from '@gravity-ui/uikit';
import {TrashBin} from '@gravity-ui/icons';
import {DataTable} from '../../shared';
import {ClientVersionModel, ClientVersionStore} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {DeleteClientVersionModal} from '../../features';
import styles from './ClientVersionsTable.module.scss';

interface ClientVersionsTableProps {
  versions: ClientVersionModel[] | undefined;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

const getStoreDisplayName = (store: ClientVersionStore | string): string => {
  const storeNames: Record<ClientVersionStore, string> = {
    [ClientVersionStore.AppleAppStore]: 'Apple App Store',
    [ClientVersionStore.GooglePlay]: 'Google Play',
    [ClientVersionStore.HuaweiAppGallery]: 'Huawei AppGallery',
    [ClientVersionStore.RuStore]: 'RuStore',
  };
  return storeNames[store as ClientVersionStore] || store;
};

export const ClientVersionsTable = ({
  versions,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ClientVersionsTableProps) => {
  const SKELETON_ROWS = 5;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingClientVersionId, setDeletingClientVersionId] = useState<string | null>(null);

  const handleDeleteVersion = (version: ClientVersionModel) => {
    setDeletingClientVersionId(version.id);
    setIsDeleteModalOpen(true);
  };

  const columns = useMemo<TableColumnConfig<ClientVersionModel>[]>(
    () => [
      {
        id: 'store',
        name: 'Клиент',
        template: (item: ClientVersionModel) => <Text variant="body-1">{getStoreDisplayName(item.store)}</Text>,
      },
      {
        id: 'versionNumber',
        name: 'Версия',
        template: (item: ClientVersionModel) => <Text variant="body-1">{item.versionNumber}</Text>,
      },
      {
        id: 'isMandatory',
        name: 'Обязательность',
        template: (item: ClientVersionModel) => <Checkbox checked={item.isMandatory} />,
      },
      {
        id: 'more',
        name: () => <div className={styles.tableHeader}></div>,
        template: (item: ClientVersionModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={TrashBin} />,
                  action: () => handleDeleteVersion(item),
                  text: 'Удалить',
                  className: styles.dropdownItemDelete,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: hasNextPage || false,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<ClientVersionModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={versions || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Версии не найдены"
        emptyStateDescription="Версии приложения не найдены"
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}

      {deletingClientVersionId && (
        <DeleteClientVersionModal
          open={isDeleteModalOpen}
          setOpen={(open) => {
            setIsDeleteModalOpen(open);
            if (!open) {
              setDeletingClientVersionId(null);
            }
          }}
          clientVersionId={deletingClientVersionId}
        />
      )}
    </div>
  );
};
